import { monaco } from "@via/components/code-editor/monaco.ts";
import { allHoverData, operatorHoverData } from "./hover-data.ts";

type Monaco = typeof monaco;

const registeredLanguages = new Set<string>();

/**
 * Registers a HoverProvider for the Groovy/Nextflow language.
 * Idempotent — skips registration if the language was already registered.
 */
export function registerGroovyHoverProvider(
  monacoInstance: Monaco,
  languageId: string
): void {
  if (registeredLanguages.has(languageId)) {
    return;
  }
  registeredLanguages.add(languageId);

  monacoInstance.languages.registerHoverProvider(languageId, {
    provideHover(
      model: monaco.editor.ITextModel,
      position: monaco.Position
    ): monaco.languages.ProviderResult<monaco.languages.Hover> {
      // Word-based lookup for keywords and identifiers
      const word = model.getWordAtPosition(position);
      if (word) {
        const hoverContent = allHoverData[word.word];
        if (hoverContent) {
          return {
            range: new monacoInstance.Range(
              position.lineNumber,
              word.startColumn,
              position.lineNumber,
              word.endColumn
            ),
            contents: [{ value: hoverContent }],
          };
        }
      }

      // Operator lookup: scan line at cursor for operator matches
      const line = model.getLineContent(position.lineNumber);
      const col = position.column - 1; // 0-based

      // Try longest operators first (3 chars → 2 → 1) to avoid partial matches
      for (const len of [3, 2, 1]) {
        const start = Math.max(0, col - len + 1);
        for (let i = start; i <= col && i + len <= line.length; i++) {
          const candidate = line.substring(i, i + len);
          if (operatorHoverData[candidate]) {
            return {
              range: new monacoInstance.Range(
                position.lineNumber,
                i + 1, // Monaco columns are 1-based
                position.lineNumber,
                i + len + 1
              ),
              contents: [{ value: operatorHoverData[candidate] }],
            };
          }
        }
      }

      return null;
    },
  });
}
